// Captured: route pathnames and clicks on interactive controls, by accessible label.
// NEVER: keystrokes, input values, or DOM content beyond that label.
import { correlationHeaders } from './client-trace'
import { registerClickSink } from './user-action-capture'
import { basename } from './utils'

interface PendingAction {
  t: number
  type: 'nav' | 'click'
  label: string
  path: string
}

const MAX_BUFFER = 50 // matches the server schema cap; oldest dropped first
const FLUSH_INTERVAL_MS = 10_000
const FLUSH_AT = 20

let buffer: PendingAction[] = []

function push(action: PendingAction): void {
  buffer.push(action)
  if (buffer.length > MAX_BUFFER) buffer = buffer.slice(-MAX_BUFFER)
  if (buffer.length >= FLUSH_AT) flush()
}

// A raw POST rather than the tRPC client, so `keepalive` lets the send finish when it races a navigation or tab close.
function flush(): void {
  if (buffer.length === 0) return
  const actions = buffer
  buffer = []
  try {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      ...correlationHeaders({ includeParentSpan: true }),
    }
    // Same `${basename}/trpc` endpoint main.tsx targets — the app can be mounted under /danchiano.
    void fetch(`${basename}/trpc/common.logClientActions?batch=1`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ 0: { actions } }),
      keepalive: true,
    }).catch(() => {
      // Swallow — a lost batch is acceptable.
    })
  } catch {
    // fetch/keepalive can throw on exotic browsers; same rule.
  }
}

/** Record a resolved navigation (pathname only — query strings can carry tokens/ids). */
export function trackNavigation(href: string): void {
  const path = href.split('?')[0] ?? href
  push({ t: Date.now(), type: 'nav', label: '', path: path.slice(0, 512) })
}

// router.tsx only calls this when the wide-events flag is on, so the sink is otherwise never registered.
export function installClientActionCapture(): void {
  if (typeof window === 'undefined') return

  // The router's onResolved fires only for SPA transitions, so the initial location is recorded here.
  push({ t: Date.now(), type: 'nav', label: '', path: window.location.pathname })

  registerClickSink((label) => {
    push({ t: Date.now(), type: 'click', label, path: window.location.pathname })
  })

  window.setInterval(flush, FLUSH_INTERVAL_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
  window.addEventListener('pagehide', flush)
}
